import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  ResetFormDataAction,
  SetFormActionWithId,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import {
  BusinessOnboardingAddress,
  BusinessOnboardingMember,
  OnboardBusinessRequest,
} from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.type';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { isDirectorComplete } from '@app/finxone-web-frontend/app/lib/utils/companies-house-directors-list.utils';
import { ProfileErrorCodes } from '@app/translations';
import {
  buildNestedPatch,
  fillBlanks,
  getNestedValue,
  isBlankValue,
} from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ButtonActionType,
  KybStatus,
  NavigationOptionEnum,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, of, tap } from 'rxjs';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { handleRedirection } from '../cta-redirection.utils';
import { validateOnboardingOtpExpiry } from './onboarding-verification.utils';
import { deriveDobFromBirthFields } from './director-actions.utils';
import { RoleState } from '../../../state/role.state';
import { PasswordService } from '../../../services/password-service/password-service.service';
import { ConfirmationHelperService } from '../../../services/confirmation-helper/confirmation-helper.service';

// Fields already surfaced as first-class members on OnboardBusinessRequest/BusinessOnboardingMember -
// everything else found on the raw form state is business data the backend doesn't have a named
// column for, so it gets bucketed into extraAttributes instead of being dropped.
const COMPANY_EXCLUDE_FIELDS = [
  'companyName',
  'companyNumber',
  'dateOfCreation',
  'companyAddress',
  'members',
  'isEmailVerified',
  'isPhoneVerified',
  'emailVerifiedAt',
  'phoneVerifiedAt',
  'verifiedEmail',
  'verifiedPhone',
  'verifiedCountryCode',
  'emailVerificationToken',
  'phoneVerificationToken',
  'formSessionId',
  'isModal',
  'selectedOfficer',
  // Bookkeeping only - identifies which member is primary (see
  // CompaniesHouseDirectorSelectionWidgetComponent.seedDirectorArrayItem, which derives each
  // member's isPrimary from this) and is also read as an email/phone fallback for the primary
  // member (see buildMemberPhone/Email below). Both are already reflected on the primary
  // member itself, so this is omitted here to avoid duplicating that data at the top level.
  'primaryOfficer',
];

const MEMBER_BASE_FIELDS = [
  'isPrimary',
  'position',
  'title',
  'firstName',
  'lastName',
  'email',
  'phone',
  'dob',
  'nationality',
  'taxResidency',
  'ownerOfBusinessPercent',
  'shareholderPercent',
];

const MEMBER_PRIMARY_ONLY_FIELDS = ['password', 'securityQuestion', 'linkedAccount'];

// Matches what the business-onboarding API actually rejects a member for when empty (confirmed
// via its 400 response) - title/taxResidency are blank-able server-side, these aren't. A director
// picked off the Companies House officer list but never routed through/completed on the wizard's
// own contact-detail pages reaches here with these still blank, so this is checked before ever
// calling the API rather than letting every such submission round-trip into a guaranteed 400.
const REQUIRED_MEMBER_FIELDS = ['position', 'email', 'phone', 'dob'];

// Company-level fields the API's request shape always expects populated - name/identificationNumber
// are non-optional on OnboardBusinessRequest itself; the companyAddress fields are what's always
// present on both a Companies-House-sourced and a manually-entered address.
const REQUIRED_COMPANY_FIELDS = ['name', 'identificationNumber'];
const REQUIRED_COMPANY_ADDRESS_FIELDS = ['country', 'address1', 'city', 'postalCode'];

// address is mapped separately (nested object); countryCode/phoneNumber are consumed by
// buildMemberPhone and folded into "phone"; officerRole is consumed by formatOfficerRole and
// folded into "position". The rest are wizard-only bookkeeping that never belongs in the API
// payload: "director"/"director_metadata" and the Companies-House-raw director* fields are
// consumed by normalisePrimaryOfficer/dob mapping above; the OTP verification flags/values are
// tracked purely for the wizard's own UI state; and "*_metadata" fields are dropdown-selection
// companions with no backend meaning.
const MEMBER_EXCLUDE_FIELDS = [
  'address',
  'countryCode',
  'phoneNumber',
  'officerRole',
  'director',
  'director_metadata',
  'directorTitle_metadata',
  'directorDateOfBirth',
  'directorBirthDay',
  'directorBirthMonth',
  'directorBirthYear',
  'nationality_metadata',
  'isEmailVerified',
  'isPhoneVerified',
  'verifiedEmail',
  'verifiedPhone',
  'verifiedCountryCode',
  'dateOfBirth',
];

function buildAddress(address: Record<string, unknown> | undefined): BusinessOnboardingAddress {
  return {
    country: (address?.['country'] as string) ?? '',
    address1: (address?.['address1'] as string) ?? '',
    address2: (address?.['address2'] as string) ?? '',
    address3: (address?.['address3'] as string) ?? '',
    address4: (address?.['address4'] as string) ?? '',
    // Companies House addresses put the city into "locality" rather than "city" - see
    // CompaniesHouseListingWidgetComponent.mapCompanyAddressFields.
    city: (address?.['city'] as string) ?? (address?.['locality'] as string) ?? '',
    // Similarly, a manually-entered AddressWidget produces "county" where Companies House
    // addresses already provide "state".
    state: (address?.['state'] as string) ?? (address?.['county'] as string) ?? '',
    postalCode: (address?.['postalCode'] as string) ?? '',
  };
}

function buildMemberPhone(record: Record<string, unknown> | undefined): string {
  if (!record) return '';
  const countryCode = (record['countryCode'] as string) || (record['country_code'] as string) || '';
  const phoneNumber = (record['phoneNumber'] as string) || (record['phone'] as string) || '';
  if (countryCode && phoneNumber) {
    return `${countryCode}${phoneNumber}`;
  }
  return (record['verifiedPhone'] as string) || phoneNumber || countryCode || '';
}

// Companies House's officerRole ("director", "corporate-nominee-director", ...) is the only
// source of a director's role/position when a member has been seeded from the officer list but
// never routed through the wizard's own position field - title-cases and despaces it into
// something presentable ("director" -> "Director", "nominee-director" -> "Nominee Director").
function formatOfficerRole(role: string | undefined): string {
  if (!role) return '';
  return role
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function buildMemberDob(record: Record<string, unknown> | undefined): string {
  if (!record) return '';

  if (record['dob'] instanceof Date) return record['dob'].toISOString().slice(0, 10);
  const rawDob = record['dob'];
  if (!isBlankValue(rawDob)) return String(rawDob).slice(0, 10);

  if (record['directorDateOfBirth']) return String(record['directorDateOfBirth']).slice(0, 10);

  if (record['date_of_birth'] instanceof Date) return record['date_of_birth'].toISOString().slice(0, 10);
  if (!isBlankValue(record['date_of_birth'])) return String(record['date_of_birth']).slice(0, 10);

  const derivedDob = deriveDobFromBirthFields(record);
  return derivedDob ?? '';
}

// The primary-director-details wizard page writes several of its own fields under names that
// don't match what businessOnboardingFlow.primaryOfficer is actually expected to carry -
// directorDateOfBirth (a full ISO datetime) instead of dob (left null), directorTitle (a raw
// dropdown value, not a display label - a separate wizard-side bug this can't fully correct) as
// a fallback for title, and shareholderPercentage (extra "-age") instead of shareholderPercent.
export function normalisePrimaryOfficer(
  primaryOfficer: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!primaryOfficer) return primaryOfficer;
  return {
    ...primaryOfficer,
    // `||` would treat a blank placeholder object (e.g. {} left by an unfilled Date-of-birth
    // group field) as a real value and never fall through - isBlankValue catches it.
    dob: isBlankValue(primaryOfficer['dob']) ? buildMemberDob(primaryOfficer) : primaryOfficer['dob'],
    title: primaryOfficer['title'] || primaryOfficer['directorTitle'] || '',
    shareholderPercent: primaryOfficer['shareholderPercent'] ?? primaryOfficer['shareholderPercentage'],
  };
}

// Number-typed form inputs (e.g. a percent field bound to a plain text/number control) commonly
// yield a string value in Angular reactive forms - the API expects ownerOfBusinessPercent/
// shareholderPercent as JSON numbers, so this coerces rather than passing a string through.
function toNumberOrUndefined(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? undefined : num;
}

const SECURITY_QUESTION_FIELD_PATTERN = /^securityQuestion(\d+)$/;

// The wizard collects any number of question/answer pairs as flat securityQuestion<N>/
// securityAnswer<N> fields (the "N" suffix is wizard-only bookkeeping - the API takes them as a
// single securityQuestion array). A pair only counts if both the question and the answer are
// present. Discovered dynamically (rather than a fixed 1/2) so the wizard can add more pairs
// without a code change here, and sorted by N so the array order matches the wizard's.
function buildSecurityQuestions(
  member: Record<string, unknown>,
): { question: string; answer: string }[] | undefined {
  const indexes = Object.keys(member)
    .map((key) => SECURITY_QUESTION_FIELD_PATTERN.exec(key)?.[1])
    .filter((index): index is string => index !== undefined)
    .map(Number)
    .sort((a, b) => a - b);

  const questions: { question: string; answer: string }[] = [];
  for (const index of indexes) {
    const question = member[`securityQuestion${index}`] as string | undefined;
    const answer = member[`securityAnswer${index}`] as string | undefined;
    if (question && answer) questions.push({ question, answer });
  }
  return questions.length > 0 ? questions : undefined;
}

// Matches any wizard-only securityQuestion<N>/securityAnswer<N>/securityQuestion<N>_metadata field
// (see buildSecurityQuestions) so extraAttributes never accumulates raw copies of what's already
// folded into the securityQuestion array above, regardless of how many pairs the wizard collects.
function isSecurityQuestionField(key: string): boolean {
  return /^security(Question|Answer)\d+(_metadata)?$/.test(key);
}

function transformMember(
  // member has already been merged with formData.primaryOfficer (when it's the matched primary)
  // and had isPrimary set/cleared by transformBusinessFormDataIntoRequestPayload before reaching
  // here, so this only needs to read member's own fields, not primaryOfficer separately.
  member: Record<string, unknown>,
  role: string | undefined,
): BusinessOnboardingMember {
  const isPrimary = !!member['isPrimary'];
  const email = (member['email'] as string) || (member['verifiedEmail'] as string) || '';
  const phone = buildMemberPhone(member);
  // A director seeded from the Companies House officer list but never routed through the
  // wizard's own position field only ever has officerRole ("director", ...) to go on - see
  // CompaniesHouseListingWidgetComponent, which seeds members verbatim from CH's raw officer data.
  const position =
    (member['position'] as string) || formatOfficerRole(member['officerRole'] as string | undefined) || '';

  const result: BusinessOnboardingMember = {
    isPrimary,
    position,
    title: (member['title'] as string) ?? '',
    firstName: (member['firstName'] as string) ?? '',
    lastName: (member['lastName'] as string) ?? '',
    email,
    phone,
    dob: buildMemberDob(member),
    nationality: (member['nationality'] as string) ?? '',
    taxResidency: (member['taxResidency'] as string) ?? '',
    ownerOfBusinessPercent: toNumberOrUndefined(member['ownerOfBusinessPercent']),
    shareholderPercent: toNumberOrUndefined(member['shareholderPercent']),
    address: buildAddress(member['address'] as Record<string, unknown> | undefined),
  };

  // Password, security question/answer and the linked account nomination only apply to the
  // primary director, who becomes the business's login user.
  if (isPrimary) {
    if (member['password'] !== undefined) result.password = member['password'] as string;
    const securityQuestion = buildSecurityQuestions(member);
    if (securityQuestion) result.securityQuestion = securityQuestion;
    if (member['linkedAccount'] !== undefined) result.linkedAccount = member['linkedAccount'] as string;
  }

  const excludedFields = new Set([
    ...MEMBER_BASE_FIELDS,
    ...MEMBER_EXCLUDE_FIELDS,
    ...MEMBER_PRIMARY_ONLY_FIELDS,
  ]);
  const extraAttributes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(member)) {
    if (excludedFields.has(key) || isSecurityQuestionField(key)) continue;
    extraAttributes[key] = value;
  }
  extraAttributes['roleName'] = role;
  if (Object.keys(extraAttributes).length > 0) {
    result.extraAttributes = extraAttributes;
  }

  return result;
}

/**
 * Maps businessOnboardingFlow's accumulated form state (company details from Companies House +
 * the director wizard's committed "members" array, see director-actions.utils.ts) into the
 * business-onboarding API's request shape. Anything on the form state that isn't one of the
 * API's named fields is preserved under extraAttributes (company-level and per-member) rather
 * than dropped.
 */
export function transformBusinessFormDataIntoRequestPayload(
  formData: Record<string, unknown>,
  role?: string,
): OnboardBusinessRequest {
  const rawMembers = Array.isArray(formData?.['members']) ? formData['members'] : [];
  const primaryOfficer = normalisePrimaryOfficer(
    formData?.['primaryOfficer'] as Record<string, unknown> | undefined,
  );
  const primaryOfficerId = primaryOfficer?.['director'];
  // "officer-<index>" (see CompaniesHouseDirectorSelectionWidgetComponent.parseOfficerIndex) is an
  // index into members[], not a field ever written onto the member itself - so index is the only
  // way to resolve it. Still also checks member.director directly, in case some other path someday
  // does stamp it onto the member (e.g. the CH officer-selection widget's own seedDirectorArrayItem).
  const primaryOfficerIndexMatch = /^officer-(\d+)$/.exec(String(primaryOfficerId ?? ''));
  const primaryOfficerIndex = primaryOfficerIndexMatch ? Number(primaryOfficerIndexMatch[1]) : null;

  // primaryOfficer is set by a different step than the members array itself (see
  // CompaniesHouseDirectorSelectionWidgetComponent) and may carry fresher/more complete details
  // for the primary director than whatever's already on members[] (e.g. collected on a later
  // wizard page). This is the single, authoritative place isPrimary gets decided for the whole
  // payload: match members[] against primaryOfficer.director, merge primaryOfficer's non-blank
  // fields into that one member (fillBlanks so it never blanks out a field member[] already has
  // that primaryOfficer doesn't carry), and make sure every other member is explicitly not primary.
  //
  // Fallbacks cover older/deprecated widget configurations that collect the primary contact details
  // directly into primaryOfficer but don't stamp a director id, or that don't create a members[]
  // array at all (the director's data only exists in primaryOfficer).
  let members: Record<string, unknown>[];
  if (rawMembers.length === 0 && primaryOfficer) {
    members = [
      {
        ...primaryOfficer,
        isPrimary: true,
      },
    ];
  } else {
    members = rawMembers.map((member: Record<string, unknown>, index: number) => {
      const isMatch =
        !!primaryOfficerId &&
        (member['director'] !== undefined
          ? member['director'] === primaryOfficerId
          : index === primaryOfficerIndex);
      const isFallbackPrimary =
        !isMatch && !primaryOfficerId && rawMembers.length === 1 && index === 0 && !!primaryOfficer;
      return isMatch || isFallbackPrimary
        ? { ...fillBlanks(primaryOfficer, member), isPrimary: true }
        : { ...member, isPrimary: false };
    });
  }

  const payload: OnboardBusinessRequest = {
    name: (formData?.['name'] as string) ?? (formData?.['companyName'] as string) ?? '',
    identificationNumber:
      (formData?.['identificationNumber'] as string) ?? (formData?.['companyNumber'] as string) ?? '',
    incorporationDate:
      (formData?.['incorporationDate'] as string) ?? (formData?.['dateOfCreation'] as string) ?? '',
    companyAddress: buildAddress(formData?.['companyAddress'] as Record<string, unknown> | undefined),
    members: members.map((member: Record<string, unknown>) => transformMember(member, role)),
    productCode: formData?.['productCode'] as string,
  };

  const extraAttributes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(formData ?? {})) {
    if (!COMPANY_EXCLUDE_FIELDS.includes(key)) {
      extraAttributes[key] = value;
    }
  }
  if (Object.keys(extraAttributes).length > 0) {
    payload.extraAttributes = extraAttributes;
  }

  return payload;
}

// Mirrors submitOnboardUser's getActionButtonOutcomeType for the individual flow: only
// SUCCESS/FAILED are terminal, everything else the KYB provider can return mid-flow
// (STARTED/IN_PROGRESS/KYB_PENDING/REVIEW_PENDING/PENDING) is still processing.
function getBusinessOnboardingOutcomeType(status: KybStatus | undefined): ActionButtonOutcomesType {
  switch (status) {
    case KybStatus.SUCCESS:
      return ActionButtonOutcomesType.SUCCESS;
    case KybStatus.FAILED:
      return ActionButtonOutcomesType.FAILURE;
    case KybStatus.PENDING:
    case KybStatus.STARTED:
    case KybStatus.IN_PROGRESS:
    case KybStatus.KYB_PENDING:
    case KybStatus.REVIEW_PENDING:
      return ActionButtonOutcomesType.PROCESSING;
    default:
      // Mirrors getActionButtonOutcomeType's default: an unrecognised/missing status is not the
      // same as an explicit FAILED from the provider, so it gets its own outcome rather than
      // being reported as a failure that didn't happen.
      return ActionButtonOutcomesType.ALTERNATE_1;
  }
}

// Mirrors onboardIndividual's pre-submit step: the current page's forms may not have been
// persisted to the store yet (or may have been written to a different store key, e.g. the global
// `response` form by deprecated PhoneNumber/Email/Birthday widgets). Read the in-memory forms
// and merge their values into businessOnboardingFlow.formData, using the form path as the nested
// key, so the payload builder works from a single authoritative form state.
function mergeCurrentPageFormsIntoBusinessFormData(
  workflowFormsService: WorkflowFormsService,
  businessFormData: Record<string, unknown>,
): Record<string, unknown> {
  const currentPageForms = workflowFormsService.getCurrentPageForms();
  if (!currentPageForms?.length) return businessFormData;

  let merged = businessFormData;
  for (const formId of currentPageForms) {
    const formValue = workflowFormsService.getFormValues(formId);
    if (!formValue || typeof formValue !== 'object' || Array.isArray(formValue)) continue;

    if (formId === FormID.BUSINESS_ONBOARDING_FLOW) {
      // Top-level form values win over the accumulated store copy, but never blank out fields
      // that are only present in the store (e.g. data from earlier wizard pages).
      merged = fillBlanks(formValue as Record<string, unknown>, merged) as Record<string, unknown>;
      continue;
    }

    const prefix = `${FormID.BUSINESS_ONBOARDING_FLOW}.`;
    if (formId.startsWith(prefix)) {
      const nestedPath = formId.slice(prefix.length);
      const currentNested = getNestedValue<Record<string, unknown>>(merged, nestedPath) ?? {};
      const updatedNested = fillBlanks(
        formValue as Record<string, unknown>,
        currentNested as Record<string, unknown>,
      );
      merged = buildNestedPatch(merged, nestedPath, updatedNested);
    }
  }

  return merged;
}

/**
 * "Submit business onboarding" - the wizard's Form Widgets have already been writing company
 * and director details straight into businessOnboardingFlow's form state as the user progresses
 * (see director-actions.utils.ts), so this just validates whatever form is on the current page,
 * reads the fully accumulated state, maps it to the business-onboarding request shape, and calls
 * the API - mirroring submitOnboardUser in onboarding-actions.utils.ts for the individual flow.
 */
export function submitBusinessOnboarding(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const router = injector.get(Router);
  const store = injector.get(Store);
  const metaDataService = injector.get(MetadataService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const passwordService = injector.get(PasswordService);
  const confirmationHelperService = injector.get(ConfirmationHelperService);

  const isValid = workflowFormsService.currentPageFormsValid();
  const currentPageValidation = workflowFormsService.validateCurrentPageForms();

  if (currentPageValidation.invalidForms?.length !== 0 || !isValid) {
    workflowFormsService.markCurrentPageFormsTouched();
    return;
  }

  let businessFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.BUSINESS_ONBOARDING_FLOW),
  )?.formData;

  if (!businessFormData || Object.keys(businessFormData).length === 0) {
    console.error('Error: Business onboarding form data is not available');
    return;
  }

  // Mirror onboardIndividual: ensure the current page's in-memory form values are merged into the
  // store's businessOnboardingFlow form state before we build and submit the payload. This also
  // pulls data from deprecated widgets that register a form with WorkflowFormsService but write to
  // the global `response` store key, by taking the form value (which is the source of truth) and
  // merging it into the correct nested path.
  businessFormData = mergeCurrentPageFormsIntoBusinessFormData(workflowFormsService, businessFormData);

  store.dispatch(
    new SetFormActionWithId(
      {
        type: '',
        formData: businessFormData,
      },
      FormID.BUSINESS_ONBOARDING_FLOW,
    ),
  );

  const role = store.selectSnapshot(RoleState.getRole)?.role;

  const payload = transformBusinessFormDataIntoRequestPayload(businessFormData, role);
  const passwordObjValue = passwordService.consumeAll();
  const primaryDirector = payload.members.find((member) => member.isPrimary);
  if (passwordObjValue?.['password'] && primaryDirector) {
    primaryDirector['password'] = passwordObjValue['password'];
  } else {
    alertHandlerService.showAlertFn('error', 'Password not found, please provide again');
    confirmationHelperService.setReconfirmationConfig({
      redirectUrl: router.url,
      reconfirmationType: 'password',
    });
    handleRedirection(
      widgetProperties,
      injector,
      ActionButtonOutcomesType.ALTERNATE_3,
      true,
      true,
      ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
    );
    return;
  }

  const abortSubmission = (reason: string) => {
    console.error(`Error: ${reason} - aborting submission instead of sending a request the API will reject`);
    alertHandlerService.showAlertFn('error', `${reason}`);
    const navigationConfig =
      widgetProperties?.actionNavigationConfigurations?.[ActionButtonOutcomesType.ALTERNATE_1];
    if (navigationConfig && navigationConfig.selectedNavigationOption !== NavigationOptionEnum.USE_AS_BLANK) {
      handleRedirection(
        widgetProperties,
        injector,
        ActionButtonOutcomesType.ALTERNATE_1,
        true,
        true,
        ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
      );
    }
  };

  if (!isDirectorComplete(payload, REQUIRED_COMPANY_FIELDS)) {
    abortSubmission(`company is missing one of ${REQUIRED_COMPANY_FIELDS.join(', ')}`);
    return;
  }
  if (!isDirectorComplete(payload.companyAddress, REQUIRED_COMPANY_ADDRESS_FIELDS)) {
    abortSubmission(`companyAddress is missing one of ${REQUIRED_COMPANY_ADDRESS_FIELDS.join(', ')}`);
    return;
  }

  const incompleteMemberIndex = payload.members.findIndex(
    (member) => !isDirectorComplete(member, REQUIRED_MEMBER_FIELDS),
  );
  if (incompleteMemberIndex !== -1) {
    abortSubmission(
      `Member [${payload.members[incompleteMemberIndex].firstName} ${payload.members[incompleteMemberIndex].lastName}] is missing one of ${REQUIRED_MEMBER_FIELDS.join(', ')} fields`,
    );
    return;
  }

  const ctaAction = widgetProperties.buttonActionType as string;

  const primaryOfficer = businessFormData?.['primaryOfficer'] as Record<string, unknown> | undefined;
  const doSubmit = () => {
    payload.emailVerificationToken = (businessFormData?.['emailVerificationToken'] ??
      primaryOfficer?.['emailVerificationToken']) as string | undefined;
    payload.phoneVerificationToken = (businessFormData?.['phoneVerificationToken'] ??
      primaryOfficer?.['phoneVerificationToken']) as string | undefined;

    ctaButtonSignalService.setSignal({ [ctaAction]: false });

    metaDataService
      .onboardBusiness(payload)
      .pipe(
        tap((response) => {
          handleRedirection(
            widgetProperties,
            injector,
            getBusinessOnboardingOutcomeType(response?.status),
            true,
            true,
            ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
          );

          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          store.dispatch(new ResetFormDataAction());
        }),
        catchError((err) => {
          // Log status/message only — the error body echoes the onboarding payload (PII)
          console.error(
            `Business onboarding failed: ${err?.status ?? ''} ${err?.message ?? 'unknown error'}`,
          );

          const alertCodes = err?.error?.alertCodes;
          if (alertCodes === ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED) {
            ctaButtonSignalService.setSignal({ [ctaAction]: true });
            validateOnboardingOtpExpiry({
              formId: FormID.BUSINESS_ONBOARDING_FLOW,
              widgetProperties,
              injector,
              isRetryAfterServerRejection: true,
              submit: doSubmit,
              buttonActionType: ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
            });
            return of(null);
          }

          if (alertCodes) {
            findAndShowAlertFromCode(store, router, alertHandlerService, [alertCodes]);
          }
          handleRedirection(
            widgetProperties,
            injector,
            ActionButtonOutcomesType.ALTERNATE_1,
            true,
            true,
            ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
          );

          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          return of(null);
        }),
      )
      .subscribe();
  };

  validateOnboardingOtpExpiry({
    formId: FormID.BUSINESS_ONBOARDING_FLOW,
    widgetProperties,
    injector,
    submit: doSubmit,
    buttonActionType: ButtonActionType.BUSINESS_ONBOARDING_SUBMIT,
  });
}
